import { useLocation } from 'wouter';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
// Removido useToast - agora usando funções utilitárias do CartContext
import { useCartContext } from '@/contexts/CartContext';
import { Trash2, Plus, Minus, ShoppingBag, ArrowRight } from 'lucide-react';

export default function CartPage() {
  const [location, setLocation] = useLocation();
  // Removido useToast - toasts são gerenciados pelo CartContext
  const { cart, isLoading: loading, totalAmount, totalItems, updateQuantity, removeFromCart, clearCart } = useCartContext();


  const proceedToCheckout = () => {

    if (!cart || !cart.items || cart.items.length === 0) {
      console.log('❌ [CartPage] proceedToCheckout - carrinho vazio ou inválido');
      return;
    }

    setLocation('/checkout');
  };

  if (loading) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-4">
              {[1, 2, 3].map((i) => (
                <Card key={i}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      <Skeleton className="w-20 h-20" />
                      <div className="flex-1 space-y-2">
                        <Skeleton className="h-4 w-3/4" />
                        <Skeleton className="h-4 w-1/2" />
                        <Skeleton className="h-8 w-24" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
            <div>
              <Card>
                <CardContent className="p-4 space-y-4">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-10 w-full" />
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!cart || !cart.items || cart.items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <ShoppingBag className="w-16 h-16 mx-auto text-muted-foreground mb-4" />
          <h1 className="text-2xl font-bold mb-2">Seu carrinho está vazio</h1>
          <p className="text-primary mb-6">
            Adicione algumas fotos ao seu carrinho para continuar.
          </p>
          <Button onClick={() => setLocation('/events')} size="lg">
            Explorar Eventos
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-3xl font-bold">
            Carrinho ({totalItems} {totalItems === 1 ? 'item' : 'itens'})
          </h1>
          <Button variant="outline" onClick={clearCart}>
            Limpar Carrinho
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Lista de Itens */}
          <div className="lg:col-span-2 space-y-4">
            {(cart.items || []).map((item) => {

              return (
                <Card key={item.id}>
                  <CardContent className="p-4">
                    <div className="flex gap-4">
                      {/* Thumbnail da foto */}
                      <div className="flex-shrink-0">
                        <img
                          src={item.photo.storageurl}
                          alt={`Foto do evento ${item.photo.event?.title}`}
                          className="w-20 h-20 object-cover rounded cursor-pointer"
                          onClick={() => setLocation(`/photos/${item.photo.id}`)}
                        />
                      </div>

                      {/* Detalhes do item */}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold truncate">
                          {item.photo.event?.title || 'Evento'}
                        </h3>
                        <p className="text-sm text-primary mb-2">
                          Resolução: {item.photo.resolution}
                        </p>

                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.id, item.quantity - 1)}
                              disabled={loading}
                            >
                              <Minus className="w-4 h-4" />
                            </Button>

                            <span className="w-8 text-center font-medium">
                              {item.quantity}
                            </span>

                            <Button
                              variant="outline"
                              size="icon"
                              className="h-8 w-8"
                              onClick={() => updateQuantity(item.id, item.quantity + 1)}
                              disabled={loading}
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                          </div>

                          <div className="text-right">
                            <div className="font-semibold">
                              R$ {(Number(item.unitPrice) * item.quantity).toFixed(2)}
                            </div>
                            {item.quantity > 1 && (
                              <div className="text-sm text-primary">
                                R$ {Number(item.unitPrice).toFixed(2)} cada
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Botão remover */}
                      <div className="flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => removeFromCart(item.id)}
                          disabled={loading}
                          className="text-destructive hover:text-destructive"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Resumo do Pedido */}
          <div>
            <Card>
              <CardHeader>
                <CardTitle>Resumo do Pedido</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal ({totalItems} {totalItems === 1 ? 'item' : 'itens'})</span>
                    <span>R$ {totalAmount.toFixed(2)}</span>
                  </div>

                  <Separator />

                  <div className="flex justify-between font-semibold">
                    <span>Total</span>
                    <span>R$ {totalAmount.toFixed(2)}</span>
                  </div>
                </div>

                <Button
                  onClick={proceedToCheckout}
                  className="w-full"
                  size="lg"
                >
                  Finalizar Compra
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>

                <div className="text-xs text-muted-foreground text-center">
                  Pagamento seguro via Stripe
                </div>
              </CardContent>
            </Card>

            {/* Informações adicionais */}
            <Card className="mt-4">
              <CardContent className="p-4">
                <h4 className="font-semibold mb-2">Informações</h4>
                <ul className="text-sm text-primary space-y-1">
                  <li>• Download em alta resolução</li>
                  <li>• Sem marca d'água</li>
                  <li>• Licença para uso pessoal</li>
                  <li>• Suporte 24/7</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}